/**
 * Search Page Observability Service
 *
 * Tracks search page load failure events.
 * Integrates with NewRelic Browser APM for monitoring search health.
 * tenantId is auto-detected by the event-tracker from hostname when not provided.
 */

import { trackEvent } from '../utils/event-tracker';
import { SEARCH_PAGE_LOAD_FAILURE } from '../constants/search-page-events';

/**
 * Serializable filter data for observability (e.g. from listing params).
 */
export type SearchPageFilterData = Record<string, unknown>;

/**
 * Parameters for tracking search page load failure
 */
export interface SearchPageLoadFailureParams {
  /** The error that caused the failure (message is derived inside the service) */
  error: unknown;

  /** Filter/search params used for the request, if available */
  filterData?: SearchPageFilterData;
}

function errorToString(error: unknown): string {
  if (error == null) {
    return 'Unknown error';
  }
  if (typeof error === 'string') {
    return error;
  }
  if (error instanceof Error) {
    return error.message || String(error);
  }
  try {
    return JSON.stringify(error);
  } catch {
    return String(error);
  }
}

function sanitizeFilterData(filter: Record<string, unknown> | undefined): SearchPageFilterData {
  if (filter == null) {
    return {};
  }
  try {
    return JSON.parse(JSON.stringify(filter)) as SearchPageFilterData;
  } catch {
    return {};
  }
}

/**
 * Search Page Observability Service
 *
 * Pure service with no React dependencies.
 * Called from React components for tracking.
 */
export const SearchPageObservabilityService = {
  /**
   * Track search page load failure.
   * tenantId is auto-detected by the event-tracker (from hostname / config).
   *
   * Attributes tracked: errorMessage, filterData (optional),
   * plus standard attributes (tenantId, timestamp, environment, deviceId, etc.).
   */
  trackLoadFailure(params: SearchPageLoadFailureParams): void {
    const { error, filterData } = params;
    trackEvent(SEARCH_PAGE_LOAD_FAILURE, {
      errorMessage: errorToString(error),
      ...(filterData != null && Object.keys(filterData).length > 0 && { filterData: sanitizeFilterData(filterData) }),
    });
  },
} as const;
